package com.gloopgo.game.domain

import com.gloopgo.game.engine.GameConstants
import com.gloopgo.game.engine.Vector2D
import kotlin.math.max
import kotlin.random.Random

/**
 * Calculates distributed pellet spawn intervals and uniform map coordinates across the whole world.
 *
 * Distributed Spawning Decision:
 * Each connected client calculates spawn timing using:
 * `perClientSpawnInterval = baseInterval * playerCount`
 *
 * Catch-up Rule:
 * When the observed pellet count drops significantly below target (e.g. < 50%),
 * the interval is accelerated to rapidly restore world density.
 */
class PelletSpawnCalculator(
    val baseIntervalMs: Long = 600L,
    val maxPellets: Int = GameConstants.PELLET_COUNT,
    val worldWidth: Float = GameConstants.WORLD_WIDTH,
    val worldHeight: Float = GameConstants.WORLD_HEIGHT,
    private val random: Random = Random.Default
) {
    /**
     * Determines whether this client should attempt to spawn a pellet.
     */
    fun shouldSpawn(currentPelletCount: Int): Boolean {
        return currentPelletCount < maxPellets
    }

    /**
     * Calculates the time to wait between pellet spawns for this client.
     */
    fun calculateSpawnInterval(currentPelletCount: Int, playerCount: Int): Long {
        val effectivePlayers = max(1, playerCount)
        val standardInterval = baseIntervalMs * effectivePlayers

        // Catch-up acceleration if world density dropped below 50%
        val densityRatio = currentPelletCount.toFloat() / maxPellets.toFloat()
        return if (densityRatio < 0.5f) {
            val catchUpFactor = max(0.3f, densityRatio * 2f)
            (standardInterval * catchUpFactor).toLong()
        } else {
            standardInterval
        }
    }

    /**
     * Generates a uniformly distributed random position across the entire world bounds.
     */
    fun generateRandomPosition(padding: Float = 50f): Vector2D {
        val x = random.nextFloat() * (worldWidth - 2 * padding) + padding
        val y = random.nextFloat() * (worldHeight - 2 * padding) + padding
        return Vector2D(x, y)
    }

    /**
     * Picks a random pellet color from the balanced palette.
     */
    fun pickPelletColor(): Long {
        return GameConstants.VIBRANT_COLORS.random(random)
    }

    /**
     * Generates a random pellet mass between 1 and 10 (inclusive).
     */
    fun generateRandomMass(): Float {
        return random.nextInt(1, 11).toFloat()
    }

    /**
     * Picks a random food emoji for pellets with mass 10.
     */
    fun pickFoodEmoji(mass: Float): String {
        return if (mass >= 10f) GameConstants.FOOD_EMOJIS.random(random) else ""
    }

    /**
     * Generates mass, emoji, and color for a new pellet, with a chance to spawn a hazard pellet (-10 mass, 💩/💣).
     */
    fun generatePelletAttributes(): Triple<Float, String, Long> {
        val isHazard = random.nextFloat() < GameConstants.HAZARD_SPAWN_CHANCE
        return if (isHazard) {
            val emoji = GameConstants.HAZARD_EMOJIS.random(random)
            val color = if (emoji == "💩") GameConstants.HAZARD_COLOR_POOP else GameConstants.HAZARD_COLOR_BOMB
            Triple(GameConstants.HAZARD_PELLET_MASS, emoji, color)
        } else {
            val mass = generateRandomMass()
            val emoji = pickFoodEmoji(mass)
            val color = pickPelletColor()
            Triple(mass, emoji, color)
        }
    }
}
